import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { IGroupAttendanceSummary } from 'core/hooks';
import { KpiCard, KpiGrid } from 'molecules';
import { PATHS } from 'router/config';

import * as S from '../../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from '../../_detailShared/detail.styled';
import { GroupColors } from './groupColors';

interface IAttendanceCardProps {
  summary?: IGroupAttendanceSummary;
}

const ExpandSummary = styled.summary`
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${GroupColors.textSecondary};
`;

const AttendedCell = styled.td`
  color: ${GroupColors.success};
  font-weight: 600;
`;

const MissedCell = styled.td<{ hasMissed: boolean }>`
  color: ${({ hasMissed }) =>
    hasMissed ? GroupColors.danger : GroupColors.textMuted};
  font-weight: ${({ hasMissed }) => (hasMissed ? 600 : 400)};
`;

/**
 * AttendanceCard — backed by `groupAttendanceSummary(groupId)`
 * (backend TZ §G3). Renders a 3-card KPI strip (lessons passed,
 * average attendance, below-threshold count) plus a collapsible
 * per-student table sorted worst-first so the operator immediately
 * sees who's slipping. Each student row links to StudentDetail.
 */
export const AttendanceCard = memo(({ summary }: IAttendanceCardProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [expanded, setExpanded] = useState(false);

  const sortedStudents = useMemo(
    () =>
      [...(summary?.students || [])]
        .filter((s): s is NonNullable<typeof s> => Boolean(s))
        .sort((a, b) => (a.attendanceRate ?? 0) - (b.attendanceRate ?? 0)),
    [summary?.students],
  );

  if (!summary) {
    return (
      <S.Card>
        <S.CardTitle>{t('pages.groupDetail.attendance.cardTitle')}</S.CardTitle>
        <S.SubTitle>{t('pages.groupDetail.attendance.noData')}</S.SubTitle>
      </S.Card>
    );
  }

  const totalLessons = summary.totalLessons ?? 0;
  const passedLessons = summary.passedLessons ?? 0;
  const avg =
    Math.round(((summary.averageAttendance ?? 0) as number) * 100) / 100;
  const below = summary.studentsBelowThresholdCount ?? 0;
  const threshold = summary.threshold ?? 50;

  return (
    <S.Card>
      <S.CardTitle>{t('pages.groupDetail.attendance.cardTitle')}</S.CardTitle>
      <div style={{ marginTop: 8 }}>
        <KpiGrid columns={3}>
          <KpiCard
            label={t('pages.groupDetail.attendance.kpi.lessons')}
            value={`${passedLessons} / ${totalLessons}`}
          />
          <KpiCard
            label={t('pages.groupDetail.attendance.kpi.average')}
            value={`${avg}%`}
            accent={avg >= threshold ? GroupColors.success : GroupColors.warn}
          />
          <KpiCard
            label={t('pages.groupDetail.attendance.kpi.belowThreshold', {
              threshold,
            })}
            value={below}
            accent={below > 0 ? GroupColors.danger : undefined}
          />
        </KpiGrid>
      </div>

      {sortedStudents.length === 0 ? null : (
        <details
          open={expanded}
          onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
          style={{ marginTop: 12 }}
        >
          <ExpandSummary>
            {t('pages.groupDetail.attendance.perStudent', {
              count: sortedStudents.length,
            })}
          </ExpandSummary>
          <div style={{ marginTop: 8 }}>
            <L.SmallTable>
              <thead>
                <tr>
                  <th>{t('pages.groupDetail.attendance.col.student')}</th>
                  <th>{t('pages.groupDetail.attendance.col.attended')}</th>
                  <th>{t('pages.groupDetail.attendance.col.missed')}</th>
                  <th>{t('pages.groupDetail.attendance.col.rate')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedStudents.map((s) => {
                  const rate =
                    Math.round(((s.attendanceRate ?? 0) as number) * 10) / 10;
                  return (
                    <tr key={s.studentId}>
                      <td>
                        <L.ChipLink
                          type="button"
                          onClick={() =>
                            history.push(
                              PATHS.studentDetail.replace(':id', s.studentId),
                            )
                          }
                        >
                          {s.fullName || '—'}
                        </L.ChipLink>
                      </td>
                      <AttendedCell>
                        {s.attended ?? 0} / {s.expectedLessons ?? 0}
                      </AttendedCell>
                      <MissedCell hasMissed={(s.missed ?? 0) > 0}>
                        {s.missed ?? 0}
                      </MissedCell>
                      <td>
                        <L.Tag tone={s.belowThreshold ? 'danger' : 'ok'}>
                          {rate}%
                        </L.Tag>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </L.SmallTable>
          </div>
        </details>
      )}
    </S.Card>
  );
});
